import React from "react";
import styled from "styled-components";
import { Card, Row, Col, Progress, Tag } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  AppstoreOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { KpiBatchSynthesis } from "../../types/kpiBatchInfo";
import { formatBatchDate } from "../../utils/kpiBatchUtils";

const StatCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  height: 100%;
  .ant-card-body {
    padding: 16px;
  }
`;

const StatTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const StatValue = styled.div<{ $color?: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => props.$color || "#111827"};
  line-height: 1.2;
`;

const StatSubtext = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 6px;
`;

const CategoryPill = styled.div<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: ${(props) => (props.$active ? "#e6f4ff" : "#f3f4f6")};
  border: 1px solid ${(props) => (props.$active ? "#91caff" : "#e5e7eb")};
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    border-color: #1677ff;
  }
`;

type Props = {
  synthesis: KpiBatchSynthesis;
  selectedCategory?: string;
  onSelectCategory?: (category: string) => void;
};

const AdminBatchSynthesisCards: React.FC<Props> = ({
  synthesis,
  selectedCategory,
  onSelectCategory,
}) => {
  const { t } = useTranslation();
  const lastRun = formatBatchDate(synthesis.lastRunAt);
  const categoriesList = Object.values(synthesis.categories);

  return (
    <div style={{ marginBottom: "20px" }}>
      <Row gutter={[16, 16]}>
        {/* Total syncs */}
        <Col xs={24} sm={12} md={6}>
          <StatCard data-testid="card-total-batches">
            <StatTitle>
              <DatabaseOutlined style={{ color: "#1677ff" }} />
              {t("admin.total_batches", "Total synchronisations")}
            </StatTitle>
            <StatValue>{synthesis.total}</StatValue>
            <StatSubtext>
              {categoriesList.length} {t("admin.table.category", "catégories")}
            </StatSubtext>
          </StatCard>
        </Col>

        {/* Success count and rate */}
        <Col xs={24} sm={12} md={6}>
          <StatCard data-testid="card-success-batches">
            <StatTitle>
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
              {t("admin.success_batches", "Synchronisations réussies")}
            </StatTitle>
            <StatValue $color="#52c41a">
              {synthesis.successCount}
              <span style={{ fontSize: "14px", fontWeight: "normal", color: "#6b7280", marginLeft: "6px" }}>
                ({synthesis.successRate}%)
              </span>
            </StatValue>
            <Progress
              percent={synthesis.successRate}
              strokeColor="#52c41a"
              showInfo={false}
              size="small"
              style={{ marginTop: 8 }}
            />
          </StatCard>
        </Col>

        {/* Errors count */}
        <Col xs={24} sm={12} md={6}>
          <StatCard data-testid="card-error-batches">
            <StatTitle>
              <CloseCircleOutlined style={{ color: synthesis.errorCount > 0 ? "#ff4d4f" : "#52c41a" }} />
              {t("admin.error_batches", "Synchronisations en erreur")}
            </StatTitle>
            <StatValue $color={synthesis.errorCount > 0 ? "#ff4d4f" : "#52c41a"}>
              {synthesis.errorCount}
              <span style={{ fontSize: "14px", fontWeight: "normal", color: "#6b7280", marginLeft: "6px" }}>
                ({synthesis.total > 0 ? Math.round(((synthesis.errorCount / synthesis.total) * 1000) / 10) : 0}%)
              </span>
            </StatValue>
            <StatSubtext>
              {synthesis.errorCount === 0 ? (
                <span style={{ color: "#52c41a" }}>{t("admin.no_error", "Aucune erreur détectée")}</span>
              ) : (
                <span style={{ color: "#ff4d4f" }}>Attention requise</span>
              )}
            </StatSubtext>
          </StatCard>
        </Col>

        {/* Last execution */}
        <Col xs={24} sm={12} md={6}>
          <StatCard data-testid="card-last-sync">
            <StatTitle>
              <ClockCircleOutlined style={{ color: "#faad14" }} />
              {t("admin.last_sync", "Dernière exécution")}
            </StatTitle>
            <StatValue style={{ fontSize: "16px" }}>
              {lastRun.formatted}
            </StatValue>
            <StatSubtext>
              {lastRun.relative && <div>{lastRun.relative}</div>}
              {synthesis.warningCount > 0 ? (
                <div
                  data-testid="synthesis-warning-count"
                  style={{
                    marginTop: 4,
                    color: "#d97706",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <WarningOutlined />
                  {synthesis.warningCount} {t("admin.delayed_batches", "batch(s) en retard")}
                </div>
              ) : (
                <div style={{ marginTop: 4, color: "#52c41a", fontSize: "11px" }}>
                  ✓ {t("admin.all_up_to_date", "Toutes à jour")}
                </div>
              )}
            </StatSubtext>
          </StatCard>
        </Col>
      </Row>

      {/* Category breakdown pill list */}
      {categoriesList.length > 0 && (
        <Card
          size="small"
          style={{ marginTop: "12px", borderRadius: "8px", background: "#fafafa" }}
        >
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 4 }}>
              <AppstoreOutlined />
              {t("admin.table.category", "Catégories")} :
            </span>

            <CategoryPill
              $active={!selectedCategory || selectedCategory === "ALL"}
              onClick={() => onSelectCategory && onSelectCategory("ALL")}
            >
              <strong>Tous</strong> ({synthesis.total})
            </CategoryPill>

            {categoriesList.map((cat) => (
              <CategoryPill
                key={cat.category}
                $active={selectedCategory === cat.category}
                onClick={() => onSelectCategory && onSelectCategory(cat.category)}
              >
                <span>{cat.category}</span>
                <Tag
                  color={cat.errorCount > 0 ? "error" : cat.warningCount > 0 ? "warning" : "success"}
                  style={{ margin: 0, padding: "0 6px", fontSize: "11px", borderRadius: "10px" }}
                >
                  {cat.total}
                  {cat.errorCount > 0 && ` (${cat.errorCount} err)`}
                  {cat.warningCount > 0 && ` (${cat.warningCount} retard)`}
                  {cat.errorCount === 0 && cat.warningCount === 0 && " ✓"}
                </Tag>
              </CategoryPill>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminBatchSynthesisCards;
